import { useContext, useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AlquileresContext } from "../../../../context/AlquileresContext";
import { getContratoById, updateContrato } from "../../../../application/contratos";
import { uploadContratoArchivo, deleteContratoArchivo, esWord } from "../../../../application/contratoFiles";
import DocumentoViewer from "../../../../components/DocumentoViewer";
import {
  calcularComision,
  calcularTotalPropietario,
} from "../../../../utils/rentalCalculations";
import QuickAddLocador from "../../../../components/QuickAddLocador/QuickAddLocador";
import QuickAddLocatario from "../../../../components/QuickAddLocatario/QuickAddLocatario";
import SearchableSelect from "../../../../components/SearchableSelect";
import MoneyInput from "../../../../components/MoneyInput";
import { nombreCompleto } from "../../../../utils/nombre";
import { agregarTramoMonto, corregirTramoVigente } from "../../../../utils/montoHistorico";
import { FaArrowLeft, FaPlusCircle, FaFilePdf, FaFileWord, FaTrash, FaUpload, FaSpinner } from "react-icons/fa";
import { ClipLoader } from "react-spinners";

const primerDiaDelMes = () => {
  const hoy = new Date();
  return `${hoy.getFullYear()}-${String(hoy.getMonth() + 1).padStart(2, "0")}-01`;
};

function ContratoEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { locadores, locatarios, refreshContratos, refreshLocadores, refreshLocatarios } =
    useContext(AlquileresContext);

  const [showAddLocador, setShowAddLocador] = useState(false);
  const [showAddLocatario, setShowAddLocatario] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [pdfUploading, setPdfUploading] = useState(false);
  const [pdfError, setPdfError] = useState(null);
  const [docViewer, setDocViewer] = useState(null);
  const pdfInputRef = useRef(null);
  // Contrato tal como estaba al abrir el form: hace falta para saber si el monto
  // cambio y para sembrar el historial con el monto viejo.
  const [contratoOriginal, setContratoOriginal] = useState(null);
  // Cuando el usuario cambia el monto hay que saber desde cuando rige: si es un
  // cambio de precio, los meses anteriores tienen que seguir con el monto viejo.
  const [vigencia, setVigencia] = useState({ desde: primerDiaDelMes(), esCorreccion: false });

  const [form, setForm] = useState({
    locadorId: "",
    locadorNombre: "",
    locatarioId: "",
    locatarioNombre: "",
    propiedad: "",
    montoActual: "",
    moneda: "ARS",
    comisionPorcentaje: 5,
    comisionMonto: 0,
    totalPropietario: 0,
    fechaInicio: "",
    fechaVencimiento: "",
    valorInicial: "",
    aumento: { tipo: "IPC", frecuencia: "TRIMESTRAL", proximoAumento: "" },
    estado: "ACTIVO",
    observaciones: "",
    pdfContrato: null,
  });

  useEffect(() => {
    const loadContrato = async () => {
      try {
        const data = await getContratoById(id);
        if (data) {
          setContratoOriginal(data);
          setForm({
            locadorId: data.locadorId || "",
            locadorNombre: data.locadorNombre || "",
            locatarioId: data.locatarioId || "",
            locatarioNombre: data.locatarioNombre || "",
            propiedad: data.propiedad || "",
            montoActual: data.montoActual || "",
            moneda: data.moneda || "ARS",
            comisionPorcentaje: data.comisionPorcentaje || 5,
            comisionMonto: data.comisionMonto || 0,
            totalPropietario: data.totalPropietario || 0,
            fechaInicio: data.fechaInicio || "",
            fechaVencimiento: data.fechaVencimiento || "",
            valorInicial: data.valorInicial || "",
            aumento: {
              tipo: data.aumento?.tipo || "IPC",
              frecuencia: data.aumento?.frecuencia || "TRIMESTRAL",
              proximoAumento: data.aumento?.proximoAumento || "",
              fechaUltimoAumento: data.aumento?.fechaUltimoAumento || data.fechaInicio || "",
            },
            estado: data.estado || "ACTIVO",
            observaciones: data.observaciones || "",
            pdfContrato: data.pdfContrato || null,
          });
        }
      } catch (error) {
        console.error("Error al cargar contrato:", error);
      } finally {
        setLoading(false);
      }
    };
    loadContrato();
  }, [id]);

  useEffect(() => {
    const monto = Number(form.montoActual) || 0;
    const porc = Number(form.comisionPorcentaje) || 0;
    const comision = calcularComision(monto, porc);
    const total = calcularTotalPropietario(monto, comision);
    setForm((prev) => ({ ...prev, comisionMonto: comision, totalPropietario: total }));
  }, [form.montoActual, form.comisionPorcentaje]);

  const updateField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const updateAumento = (field, value) => {
    setForm((prev) => ({
      ...prev,
      aumento: { ...prev.aumento, [field]: value },
    }));
  };

  const handleLocadorChange = (locId) => {
    const loc = locadores.find((l) => l.idFirebase === locId);
    setForm((prev) => ({
      ...prev,
      locadorId: locId,
      locadorNombre: loc ? nombreCompleto(loc) : "",
    }));
  };

  const handleLocatarioChange = (locId) => {
    const loc = locatarios.find((l) => l.idFirebase === locId);
    setForm((prev) => ({
      ...prev,
      locatarioId: locId,
      locatarioNombre: loc ? nombreCompleto(loc) : "",
    }));
  };

  const handlePdfChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPdfError(null);
    setPdfUploading(true);
    try {
      const meta = await uploadContratoArchivo(file, id, form.pdfContrato);
      // Persist immediately so the file is reachable even if the user closes
      // the page without clicking "Actualizar".
      await updateContrato(id, { pdfContrato: meta });
      setForm((prev) => ({ ...prev, pdfContrato: meta }));
      await refreshContratos();
    } catch (error) {
      console.error("Error al subir PDF:", error);
      setPdfError(error.message || "No se pudo subir el archivo");
    } finally {
      setPdfUploading(false);
      if (pdfInputRef.current) pdfInputRef.current.value = "";
    }
  };

  const handlePdfDelete = async () => {
    if (!form.pdfContrato) return;
    if (!window.confirm("¿Eliminar el documento del contrato?")) return;
    setPdfError(null);
    try {
      await deleteContratoArchivo(form.pdfContrato);
      await updateContrato(id, { pdfContrato: null });
      setForm((prev) => ({ ...prev, pdfContrato: null }));
      await refreshContratos();
    } catch (error) {
      console.error("Error al eliminar PDF:", error);
      setPdfError(error.message || "No se pudo eliminar el archivo");
    }
  };

  const montoOriginal = Number(contratoOriginal?.montoActual) || 0;
  const montoCambio =
    !!contratoOriginal && montoOriginal > 0 && Number(form.montoActual) !== montoOriginal;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const data = {
        ...form,
        montoActual: Number(form.montoActual),
        valorInicial: Number(form.valorInicial) || Number(form.montoActual),
        comisionPorcentaje: Number(form.comisionPorcentaje),
        comisionMonto: Number(form.comisionMonto),
        totalPropietario: Number(form.totalPropietario),
      };

      // Si cambio el monto, registrarlo en el historial para que los meses ya
      // pasados no queden con el precio nuevo. Una correccion pisa el tramo
      // vigente; un cambio de precio abre un tramo desde la fecha elegida.
      if (montoCambio) {
        if (vigencia.esCorreccion) {
          const corregido = corregirTramoVigente(contratoOriginal, data.montoActual);
          if (corregido) data.historialMontos = corregido;
        } else {
          data.historialMontos = agregarTramoMonto(contratoOriginal, {
            desde: vigencia.desde || primerDiaDelMes(),
            monto: data.montoActual,
            comisionPorcentaje: data.comisionPorcentaje || null,
            comisionMonto: data.comisionMonto,
            totalPropietario: data.totalPropietario,
            motivo: "EDICION",
            registradoEn: new Date().toISOString(),
          });
        }
      }
      await updateContrato(id, data);
      await refreshContratos();
      navigate("/dashboard/alquileres/contratos");
    } catch (error) {
      console.error("Error al actualizar contrato:", error);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <ClipLoader color="#2589BD" size={40} />
      </div>
    );
  }

  const inputClass =
    "w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100 focus:ring-2 focus:ring-primary focus:border-transparent";
  const labelClass = "block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1";

  return (
    <div className="px-4 md:px-6 py-4 space-y-6 max-w-4xl mx-auto">
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate(-1)}
          className="p-2 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
        >
          <FaArrowLeft />
        </button>
        <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100">
          Editar Contrato
        </h1>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Partes */}
        <section className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-5 space-y-4">
          <h2 className="text-base font-semibold text-gray-800 dark:text-gray-100 border-b border-gray-100 dark:border-gray-700 pb-2">
            Partes del Contrato
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Locador *</label>
              <div className="flex gap-2">
                <div className="flex-1">
                  <SearchableSelect
                    options={locadores}
                    value={form.locadorId}
                    onChange={handleLocadorChange}
                    getLabel={nombreCompleto}
                    placeholder="Buscar locador..."
                    required
                  />
                </div>
                <button
                  type="button"
                  onClick={() => setShowAddLocador(true)}
                  className="px-3 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
                >
                  <FaPlusCircle className="text-primary w-4 h-4" />
                </button>
              </div>
            </div>
            <div>
              <label className={labelClass}>Locatario *</label>
              <div className="flex gap-2">
                <div className="flex-1">
                  <SearchableSelect
                    options={locatarios}
                    value={form.locatarioId}
                    onChange={handleLocatarioChange}
                    getLabel={nombreCompleto}
                    placeholder="Buscar locatario..."
                    required
                  />
                </div>
                <button
                  type="button"
                  onClick={() => setShowAddLocatario(true)}
                  className="px-3 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
                >
                  <FaPlusCircle className="text-primary w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
          <div>
            <label className={labelClass}>Propiedad *</label>
            <input
              type="text"
              value={form.propiedad}
              onChange={(e) => updateField("propiedad", e.target.value)}
              className={inputClass}
              required
            />
          </div>
        </section>

        {/* Datos Economicos */}
        <section className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-5 space-y-4">
          <h2 className="text-base font-semibold text-gray-800 dark:text-gray-100 border-b border-gray-100 dark:border-gray-700 pb-2">
            Datos Economicos
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className={labelClass}>Monto Mensual *</label>
              <MoneyInput
                value={form.montoActual}
                onChange={(v) => updateField("montoActual", v)}
                className={inputClass}
                required
              />
            </div>
            <div>
              <label className={labelClass}>Moneda</label>
              <select
                value={form.moneda}
                onChange={(e) => updateField("moneda", e.target.value)}
                className={inputClass}
              >
                <option value="ARS">ARS (Pesos)</option>
                <option value="USD">USD (Dolares)</option>
              </select>
            </div>
            <div>
              <label className={labelClass}>Comision %</label>
              <input
                type="number"
                value={form.comisionPorcentaje}
                onChange={(e) => updateField("comisionPorcentaje", e.target.value)}
                className={inputClass}
                min="0"
                max="100"
                step="0.5"
              />
            </div>
          </div>
          {montoCambio && (
            <div className="rounded-lg border border-amber-200 dark:border-amber-800/60 bg-amber-50 dark:bg-amber-900/20 p-3 space-y-3">
              <p className="text-xs text-amber-700 dark:text-amber-400">
                Estas cambiando el monto de{" "}
                <strong>{form.moneda === "USD" ? "U$D" : "$"} {montoOriginal.toLocaleString("es-AR")}</strong> a{" "}
                <strong>{form.moneda === "USD" ? "U$D" : "$"} {(Number(form.montoActual) || 0).toLocaleString("es-AR")}</strong>.
              </p>
              <label className="flex items-start gap-2 text-xs text-amber-700 dark:text-amber-400 cursor-pointer">
                <input
                  type="checkbox"
                  checked={vigencia.esCorreccion}
                  onChange={(e) => setVigencia((prev) => ({ ...prev, esCorreccion: e.target.checked }))}
                  className="mt-0.5"
                />
                <span>
                  Es una correccion del monto (estaba mal cargado). Se pisa el monto vigente sin
                  tocar los periodos anteriores.
                </span>
              </label>
              {!vigencia.esCorreccion && (
                <div>
                  <label className="block text-xs font-medium text-amber-700 dark:text-amber-400 mb-1">
                    El monto nuevo rige desde
                  </label>
                  <input
                    type="date"
                    value={vigencia.desde}
                    onChange={(e) => setVigencia((prev) => ({ ...prev, desde: e.target.value }))}
                    className={inputClass}
                  />
                  <p className="text-[11px] text-amber-600 dark:text-amber-400/80 mt-1">
                    Los meses anteriores a esta fecha siguen mostrando{" "}
                    {form.moneda === "USD" ? "U$D" : "$"} {montoOriginal.toLocaleString("es-AR")} en
                    Cobros, Liquidacion y Reportes.
                  </p>
                </div>
              )}
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3">
              <p className="text-xs text-gray-500 dark:text-gray-400">Comision</p>
              <p className="text-lg font-bold text-primary">
                {form.moneda === "USD" ? "U$D" : "$"}{" "}
                {Number(form.comisionMonto).toLocaleString("es-AR")}
              </p>
            </div>
            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3">
              <p className="text-xs text-gray-500 dark:text-gray-400">Total Propietario</p>
              <p className="text-lg font-bold text-green-600 dark:text-green-400">
                {form.moneda === "USD" ? "U$D" : "$"}{" "}
                {Number(form.totalPropietario).toLocaleString("es-AR")}
              </p>
            </div>
          </div>
        </section>

        {/* Fechas */}
        <section className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-5 space-y-4">
          <h2 className="text-base font-semibold text-gray-800 dark:text-gray-100 border-b border-gray-100 dark:border-gray-700 pb-2">
            Fechas
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Fecha de Inicio *</label>
              <input
                type="date"
                value={form.fechaInicio}
                onChange={(e) => updateField("fechaInicio", e.target.value)}
                className={inputClass}
                required
              />
            </div>
            <div>
              <label className={labelClass}>Fecha de Vencimiento</label>
              <input
                type="date"
                value={form.fechaVencimiento}
                onChange={(e) => updateField("fechaVencimiento", e.target.value)}
                className={inputClass}
              />
            </div>
          </div>
        </section>

        {/* Aumentos */}
        <section className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-5 space-y-4">
          <h2 className="text-base font-semibold text-gray-800 dark:text-gray-100 border-b border-gray-100 dark:border-gray-700 pb-2">
            Aumentos
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className={labelClass}>Tipo de Aumento</label>
              <select
                value={form.aumento.tipo}
                onChange={(e) => updateAumento("tipo", e.target.value)}
                className={inputClass}
              >
                <option value="IPC">IPC</option>
                <option value="INDEX">INDEX</option>
                <option value="FIJO">FIJO</option>
              </select>
            </div>
            <div>
              <label className={labelClass}>Frecuencia</label>
              <select
                value={form.aumento.frecuencia}
                onChange={(e) => updateAumento("frecuencia", e.target.value)}
                className={inputClass}
              >
                <option value="TRIMESTRAL">Trimestral</option>
                <option value="CUATRIMESTRAL">Cuatrimestral</option>
                <option value="SEMESTRAL">Semestral</option>
                <option value="ANUAL">Anual</option>
              </select>
            </div>
            <div>
              <label className={labelClass}>Proximo Aumento</label>
              <input
                type="date"
                value={form.aumento.proximoAumento}
                onChange={(e) => updateAumento("proximoAumento", e.target.value)}
                className={inputClass}
              />
            </div>
          </div>
        </section>

        {/* Estado y Observaciones */}
        <section className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-5 space-y-4">
          <h2 className="text-base font-semibold text-gray-800 dark:text-gray-100 border-b border-gray-100 dark:border-gray-700 pb-2">
            Estado y Observaciones
          </h2>
          <div>
            <label className={labelClass}>Estado</label>
            <select
              value={form.estado}
              onChange={(e) => updateField("estado", e.target.value)}
              className={inputClass}
            >
              <option value="ACTIVO">Activo</option>
              <option value="VENCIDO">Vencido</option>
              <option value="RESCINDIDO">Rescindido</option>
            </select>
          </div>
          <div>
            <label className={labelClass}>Observaciones</label>
            <textarea
              value={form.observaciones}
              onChange={(e) => updateField("observaciones", e.target.value)}
              className={`${inputClass} h-24 resize-none`}
            />
          </div>
        </section>

        {/* PDF del Contrato */}
        <section className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-5 space-y-3">
          <h2 className="text-base font-semibold text-gray-800 dark:text-gray-100 border-b border-gray-100 dark:border-gray-700 pb-2">
            Documento del Contrato (PDF o Word)
          </h2>
          {form.pdfContrato?.url ? (
            <div className="flex flex-wrap items-center gap-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3">
              {esWord(form.pdfContrato) ? (
                <FaFileWord className="text-blue-600 w-6 h-6 flex-shrink-0" />
              ) : (
                <FaFilePdf className="text-red-500 w-6 h-6 flex-shrink-0" />
              )}
              <div className="flex-1 min-w-0">
                <button
                  type="button"
                  onClick={() => setDocViewer(form.pdfContrato)}
                  className="text-sm font-medium text-primary hover:underline break-all text-left"
                >
                  {form.pdfContrato.name || "Ver documento"}
                </button>
                {form.pdfContrato.uploadedAt && (
                  <p className="text-xs text-gray-500 mt-0.5">
                    Subido el {new Date(form.pdfContrato.uploadedAt).toLocaleDateString("es-AR")}
                  </p>
                )}
              </div>
              <button
                type="button"
                onClick={() => pdfInputRef.current?.click()}
                disabled={pdfUploading}
                className="inline-flex items-center gap-2 text-xs px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50"
              >
                <FaUpload className="w-3 h-3" /> Reemplazar
              </button>
              <button
                type="button"
                onClick={handlePdfDelete}
                disabled={pdfUploading}
                className="inline-flex items-center gap-2 text-xs px-3 py-1.5 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg disabled:opacity-50"
              >
                <FaTrash className="w-3 h-3" /> Eliminar
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => pdfInputRef.current?.click()}
              disabled={pdfUploading}
              className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 border border-dashed border-gray-300 dark:border-gray-600 rounded-lg text-sm text-gray-500 dark:text-gray-400 hover:border-primary hover:text-primary transition-colors disabled:opacity-50"
            >
              {pdfUploading ? (
                <><FaSpinner className="w-4 h-4 animate-spin" /> Subiendo...</>
              ) : (
                <><FaUpload className="w-4 h-4" /> Subir contrato (PDF o Word)</>
              )}
            </button>
          )}
          <input
            ref={pdfInputRef}
            type="file"
            accept="application/pdf,.pdf,.doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
            onChange={handlePdfChange}
            className="hidden"
          />
          {pdfUploading && form.pdfContrato && (
            <p className="text-xs text-blue-500 flex items-center gap-2">
              <FaSpinner className="w-3 h-3 animate-spin" /> Subiendo reemplazo...
            </p>
          )}
          {pdfError && (
            <p className="text-xs text-red-500">{pdfError}</p>
          )}
        </section>

        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="px-6 py-2.5 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={saving}
            className="px-6 py-2.5 text-sm bg-primary hover:bg-secondary text-white font-medium rounded-lg transition-colors disabled:opacity-50"
          >
            {saving ? "Guardando..." : "Actualizar Contrato"}
          </button>
        </div>
      </form>

      <QuickAddLocador
        isOpen={showAddLocador}
        onClose={() => setShowAddLocador(false)}
        onCreated={(loc) => {
          refreshLocadores();
          handleLocadorChange(loc.idFirebase);
        }}
      />
      <QuickAddLocatario
        isOpen={showAddLocatario}
        onClose={() => setShowAddLocatario(false)}
        onCreated={(loc) => {
          refreshLocatarios();
          handleLocatarioChange(loc.idFirebase);
        }}
      />

      {docViewer && <DocumentoViewer meta={docViewer} onClose={() => setDocViewer(null)} />}
    </div>
  );
}

export default ContratoEdit;
